import React from 'react';

import { Avatar } from 'src/components/basic';
import SVGIcon from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';

import { lang, logger, scssVariables as variables } from 'src/helpers';

import model from './model';

export default class Call extends BaseComponent {
  warn = logger.warn.bind(logger, 'Call');
  error = logger.error.bind(logger, 'Call');
  state = { time: 0, volume: 70, deaf: false };

  componentDidMount() {
    model.subscribe(this.subscription);
    this.interval = setInterval(() => {
      let { startDate } = model.getCurrent();
      if (startDate > 0) this.forceUpdate();
    }, 1000);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
    clearInterval(this.interval);
  }

  hangup = () => {
    console.log('hangup');
    let callUUID = model.state.current;
    if (!callUUID) return console.warn('hangup fail, no callUUID');
    let { state } = model.getCurrent(callUUID);
    if (state === 'ended') {
      model.setState({ current: null });
    } else {
      model.endCall({ callUUID });
    }
  };

  deaf = () => {
    this.setState({ deaf: !this.state.deaf });
  };

  volumeChange = (e) => {
    this.setState({
      volume: e.target.value,
      deaf: this.state.deaf && this.state.volume > e.target.value
    });
  };

  formatTime = (startDate, endDate) => {
    if (!endDate) endDate = Date.now();
    let totalSecs = Math.floor((endDate - startDate) / 1000);
    let mins = Math.floor(totalSecs / 60);
    let secs = totalSecs - mins * 60;
    if (mins < 10) mins = '0' + mins.toString();
    if (secs < 10) secs = '0' + secs.toString();
    return mins + ':' + secs;
  };

  getErrorMessage = ({ error, endReason, partnerName }) => {
    let errorMessage = '';
    if (error) {
      if (error === 'localStreamError') {
        errorMessage = 'Your recording device is not available';
      } else if (error === 'requestTimeouted') {
        errorMessage = `${partnerName} didn't respond`;
      } else if (error === 'disconnected') {
        errorMessage = 'Disconnected';
      } else if (error === 'connectionError') {
        errorMessage = 'Connection error';
      } else {
        console.warn('call - missing error description', error);
        errorMessage = error;
      }
    } else if (endReason) {
      if (endReason === 'localStreamError') {
        errorMessage = `${partnerName} recording device not available`;
      } else if (endReason === 'requestTimeouted') {
        errorMessage = 'Call request timeouted';
      } else if (endReason === 'disconnected') {
        errorMessage = `${partnerName} disconnected`;
      } else if (endReason === 'connectionError') {
        errorMessage = `${partnerName} connection error`;
      } else if (endReason === 'busy') {
        errorMessage = `${partnerName} is currently busy. Please try again later.`;
      } else if (endReason === 'rejected') {
        errorMessage = `${partnerName} declined`;
      } else {
        console.warn('call - missing endReason description', endReason);
        errorMessage = endReason;
      }
    }
    return errorMessage ? lang.t(errorMessage) : false;
  };

  render() {
    const callUUID = model.state.current;
    const {
      state,
      isCaller,
      isCallee,
      error,
      endReason,
      partnerName = '',
      partnerAvatar,

      video,
      localStream,
      remoteStream,
      audioEnabled,
      videoEnabled,
      remoteVideoMuted,
      startDate,
      endDate
    } = model.getListState(callUUID);

    let avatar = partnerAvatar;
    if (avatar) avatar = avatar.replace('/full_', '/350_');
    let name = partnerName;
    let isVideoCall = !!video;

    const { localVideo, remoteVideo } = this;
    if (remoteVideo && remoteStream) {
      if (remoteVideo.srcObject !== remoteStream) remoteVideo.srcObject = remoteStream;
      remoteVideo.volume = this.state.deaf ? 0 : this.state.volume / 100;
      if (remoteVideoMuted) {
        remoteVideo.style.opacity = '0';
      } else {
        remoteVideo.style.opacity = '1';
      }
    }

    if (localVideo && localStream && videoEnabled && localVideo.srcObject !== localStream) {
      localVideo.srcObject = localStream;
    }

    // console.log('Call render', { remoteVideoMuted });

    let errorMessage = this.getErrorMessage({ error, endReason, partnerName });

    if (state !== 'responsed' || errorMessage) {
      return (
        <div className={'mc-call'}>
          <div className={'mc-btn-bar'}>
            <div className={'mc-main-bar'}>
              {state === 'requested' && isCallee && (
                <div
                  className={'mc-circle-btn'}
                  style={{ transform: 'rotate(180deg)' }}
                  onClick={() => model.answerCall({ callUUID })}
                >
                  <SVGIcon iconname={'accept-call-icon'} />
                </div>
              )}
              <div className={'mc-circle-btn'} onClick={this.hangup}>
                <SVGIcon iconname={'stop-call-icon'} />
              </div>
            </div>
          </div>
          <video
            className={'mc-local-video'}
            style={{ zIndex: videoEnabled ? 1 : -1 }}
            ref={(ref) => (this.localVideo = ref)}
            autoPlay
            muted
          ></video>
          <div className={'mc-connecting'}>
            <Avatar text={name} width={100} height={100} image={avatar} />
            <div className={'mc-text'}>
              <div className='mc-call-status'>{name}</div>
            </div>
            {state === 'requested' && (
              <div className={'mc-text'}>
                <div className='mc-call-status'>
                  {isCaller ? 'Ringing' : `Incoming ${isVideoCall ? 'video ' : ''}call`}
                </div>
              </div>
            )}
            {!!errorMessage && (
              <div className={'mc-text'}>
                <div className='mc-call-status'>{errorMessage}</div>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className={`mc-call ${isVideoCall ? 'mc-show-video' : ''}`}>
          <div className={'mc-btn-bar'}>
            {startDate > 0 && (
              <div className={'mc-left'}>
                <div className={'mc-time'}>{this.formatTime(startDate, endDate)}</div>
              </div>
            )}
            <div className={'mc-main-bar'}>
              <div className={'mc-circle-btn'} onClick={model.toggleAudio}>
                <SVGIcon
                  iconname={'micro-icon'}
                  width={20}
                  opacity={1}
                  fill={
                    variables[
                      localStream && audioEnabled ? 'colors-mc-primary' : 'colors-mc-audio-wave'
                    ]
                  }
                />
              </div>
              <div className={'mc-circle-btn'} onClick={this.hangup}>
                <SVGIcon iconname={'stop-call-icon'} />
              </div>
              {isVideoCall && (
                <div className={'mc-circle-btn'} onClick={model.toggleVideo}>
                  <SVGIcon
                    iconname={'camera-video-icon'}
                    width={30}
                    fillCircle={variables['colors-mc-white']}
                    fill={
                      variables[
                        localStream && videoEnabled ? 'colors-mc-primary' : 'colors-mc-audio-wave'
                      ]
                    }
                  />
                </div>
              )}
            </div>
            <div className={'mc-volume mc-right'}>
              <div className='mc-volume-deaf' onClick={this.deaf}>
                <SVGIcon
                  iconname={'volume-icon'}
                  fill={
                    variables[
                      this.state.deaf || this.state.volume === 0
                        ? 'colors-mc-audio-wave'
                        : 'colors-mc-primary'
                    ]
                  }
                  width={15}
                  height={15}
                  volume={this.state.deaf ? 0 : parseInt(this.state.volume / 25) + 1}
                />
              </div>
              <input
                type='range'
                value={this.state.volume}
                min={0}
                className={'swiper-no-swiping'}
                style={{
                  background:
                    'linear-gradient(to right, ' +
                    variables['colors-mc-primary'] +
                    ' 0%, ' +
                    variables['colors-mc-primary'] +
                    ' ' +
                    this.state.volume +
                    '%, ' +
                    variables['colors-mc-audio-wave'] +
                    ' ' +
                    this.state.volume +
                    '%, ' +
                    variables['colors-mc-audio-wave'] +
                    ' 100%)'
                }}
                max={100}
                id='myRange'
                onChange={this.volumeChange}
              />
            </div>
          </div>
          {isVideoCall && (
            <video
              className={'mc-local-video'}
              style={{ zIndex: videoEnabled ? 1 : -1 }}
              ref={(ref) => (this.localVideo = ref)}
              autoPlay
              muted
            ></video>
          )}
          {!isVideoCall && (
            <div className={'mc-connecting'}>
              <Avatar text={name} width={100} height={100} image={avatar} />
              <div className={'mc-text'}>
                <div className='mc-call-status'>{name}</div>
              </div>
            </div>
          )}
          {isVideoCall && (
            <>
              <video
                className='mc-remote-video'
                ref={(ref) => (this.remoteVideo = ref)}
                autoPlay
              ></video>
              {remoteVideoMuted && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    // backgroundColor: 'black',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <SVGIcon
                    iconname={'camera-video-icon'}
                    width={60}
                    height={60}
                    fillCircle={variables['colors-mc-white']}
                    fill={variables['colors-mc-audio-wave']}
                  />
                </div>
              )}
            </>
          )}
        </div>
      );
    }
  }
}
